import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";

import Button from "/components/common/button";
import { addRoistatIdToFormData, checkAndLogRoistatId } from "~/utils";
import { reachGoal } from "~/utils/tracking";
import { ApiClient } from "~/api/client";
import CheckboxNew from "../../../common/checkboxNew";
import InputNew from "../../../common/inputNew";
import { Api } from "/api";

import {
  DEFAULT_REPLY_SETTINGS,
  normalizeReplySettings,
  resolveSuccessReply,
} from "./successReply";

/**
 * Определяем "интерактивный" code для InputNew по человекочитаемому имени поля
 * (Телефон/Имя/E-mail), чтобы переиспользовать встроенную в InputNew валидацию
 * и маску телефона. Для всех остальных полей возвращаем бэкендовый CODE —
 * InputNew тогда отрисует обычный input/textarea без спец-логики.
 */
export function detectInputCode(field) {
  const name = (field?.NAME || "").toLowerCase();
  if (name.includes("телефон")) return "phone";
  if (name.includes("mail") || name.includes("почта")) return "email";
  if (name.includes("фио") || name.includes("имя")) return "name";
  return field?.CODE || "";
}

// Тексты ошибок для «интерактивных» кодов, которые возвращает detectInputCode.
export const INPUT_ERROR_TEXT_BY_CODE = {
  name: "*Проверьте указанное ФИО",
  phone: "*Проверьте номер телефона",
};

/**
 * Согласие на рекламную рассылку — необязательное: без него форма отправляется.
 * В Bitrix у всех флажков REQUIRED=false, поэтому отличаем по тексту подписи.
 */
function isOptionalConsent(cb) {
  return /рекламн/i.test(cb?.LABEL || cb?.NAME || "");
}

function isHiddenSystemField(field) {
  const name = (field?.NAME || "").toLowerCase();
  return name.includes("страница") || name.includes("roistat");
}

function findFieldByName(fields, pattern) {
  return (fields || []).find((f) => pattern.test(f?.NAME || ""));
}

const emptySubmitOutcome = { isSuccess: false, message: "", reply: null };

/** Значение поля «Имя/ФИО» из введённых данных (для подстановки #ИМЯ#). */
function extractClientName(fields, values) {
  const nameField = (fields || []).find(
    (field) => detectInputCode(field) === "name",
  );
  if (!nameField) return "";
  return values?.[nameField.CODE] || "";
}

/**
 * Прежний текст успеха формы (тот же, что показывался раньше):
 * кастомный `successText` (строка/функция) или дефолтный шаблон с номером заявки.
 * Используется как fallback, когда в панели нет текстовок отбивки.
 */
function previousSuccessMessage(successText, response) {
  if (typeof successText === "function") return successText(response);
  if (successText) return successText;
  return `Заявка отправлена. Номер вашей заявки #${response.success}`;
}

/**
 * Загрузка полей Bitrix-формы по `code`, рендер инпутов/чекбоксов и отправка через Api.formsV2.sentForm.
 * `fieldValues`, чекбоксы, оферта и кнопка отправки — внутри хука (`inputFieldElements`, `checkboxOfferInner`, `submitButtonElement`).
 * Итог отправки — `result`: `{ isSuccess, message, reply }`. При успехе формируется `reply` ({ title, subtitle, body }):
 * без `successText` — универсальная отбивка по времени МСК (тексты из панели, см. successReply.js),
 * с `successText` (строка/функция) — переданный текст в том же дизайне.
 */
export function usePopupForm({
  code,
  goalOpen,
  goalSent,
  filterField,
  extraData,
  successText,
  onSuccess,
  onAfterSubmit,
  isNeedFocus = true,
  formId = "popupForm",
}) {
  const router = useRouter();
  const { data: popupData } = useSelector((state) => state.popup);

  const [form, setForm] = useState({
    title: "",
    description: "",
    button: "",
    fields: [],
  });
  const [isLoading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(emptySubmitOutcome);
  const [agreeError, setAgreeError] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [checkboxState, setCheckboxState] = useState({});
  const [replySettings, setReplySettings] = useState(DEFAULT_REPLY_SETTINGS);

  // Настройки отбивки (тексты, время работы) — редактируются в панели.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Api.server.getFormSuccessReply();
        if (!cancelled && data) setReplySettings(normalizeReplySettings(data));
      } catch (e) {
        // молча оставляем настройки по умолчанию (без текстовок)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await ApiClient.formsV2.getFields(
          code,
          Boolean(router.query.reset),
        );
        if (!cancelled && data) {
          setForm({
            title: data.title || "",
            description: data.description || "",
            button: data.button || "",
            fields: Array.isArray(data.fields) ? data.fields : [],
          });
        }
      } catch (e) {
        if (!cancelled) {
          setResult({
            isSuccess: false,
            message: e?.message || "Не удалось загрузить форму",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, router.query.reset]);

  useEffect(() => {
    if (goalOpen) {
      reachGoal(goalOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkboxFields = useMemo(
    () => form.fields.filter((field) => field?.TYPE === "checkbox"),
    [form.fields],
  );

  const hasRoistatField = useMemo(
    () => form.fields.some((field) => /roistat/i.test(field?.NAME || "")),
    [form.fields],
  );

  const inputFields = useMemo(
    () =>
      form.fields
        .filter((field) => field?.TYPE !== "checkbox")
        .filter((field) => !isHiddenSystemField(field))
        .filter((field) => (filterField ? filterField(field) : true)),
    [form.fields, filterField],
  );

  const inputFieldElements = useMemo(
    () =>
      inputFields.map((field) => {
        const { NAME, CODE, ERROR, TYPE } = field;
        const inputCode = detectInputCode(field);
        return (
          <InputNew
            key={CODE}
            code={inputCode}
            label={NAME}
            onChange={(value) =>
              setFieldValues((prev) => ({ ...prev, [CODE]: value }))
            }
            isError={ERROR}
            isMultiple={TYPE === "textarea" || TYPE === "HTML"}
            value={fieldValues[CODE] || ""}
            errorText={INPUT_ERROR_TEXT_BY_CODE[inputCode]}
          />
        );
      }),
    [inputFields, fieldValues],
  );

  const checkboxOfferInner = useMemo(() => {
    if (checkboxFields.length === 0) return null;
    // Ошибка — под каждым неотмеченным флажком; id и data-test — у первой.
    const firstMissingCode = checkboxFields.find(
      (cb) => !isOptionalConsent(cb) && !checkboxState[cb.CODE],
    )?.CODE;
    return (
      <>
        {checkboxFields.map((cb, idx) => {
          const labelHtml = cb.LABEL || cb.NAME || "";
          const isChecked = !!checkboxState[cb.CODE];
          const isFirstMissing = cb.CODE === firstMissingCode;
          return (
            <Fragment key={cb.CODE}>
              <CheckboxNew
                test={
                  idx === 0 ? "ofertaFristCheckbox" : "ofertaSecondCheckbox"
                }
                checked={isChecked}
                onChange={() => {
                  setCheckboxState((prev) => ({
                    ...prev,
                    [cb.CODE]: !prev[cb.CODE],
                  }));
                }}
                label={
                  <div
                    className="checkbox-text"
                    dangerouslySetInnerHTML={{ __html: labelHtml }}
                  />
                }
              />
              {agreeError && !isChecked && !isOptionalConsent(cb) && (
                <p
                  id={isFirstMissing ? "oferta" : undefined}
                  data-test={isFirstMissing ? "ofertaText" : undefined}
                >
                  Поставьте галочку, чтобы продолжить
                </p>
              )}
            </Fragment>
          );
        })}
      </>
    );
  }, [agreeError, checkboxFields, checkboxState]);

  useEffect(() => {
    if (!isLoading && isNeedFocus) {
      const popupElement = document.getElementById(formId);
      if (popupElement) {
        const firstInput = popupElement.querySelector(
          "input, textarea, select",
        );
        if (firstInput) {
          firstInput.focus();
        }
      }
    }
  }, [isLoading, formId]);

  const sentForm = useCallback(async () => {
    setAgreeError(false);
    const hasMissing = checkboxFields.some(
      (cb) => !isOptionalConsent(cb) && !checkboxState[cb.CODE],
    );
    if (hasMissing) {
      setAgreeError(true);
      return;
    }

    setIsProcessing(true);
    setResult(emptySubmitOutcome);

    const checkboxPayload = {};
    Object.keys(checkboxState).forEach((key) => {
      if (checkboxState[key]) checkboxPayload[key] = 1;
    });

    const extras = extraData
      ? extraData({ fieldValues, popupData, form }) || {}
      : {};
    let payload = { ...fieldValues, ...extras, ...checkboxPayload };

    const pageField = findFieldByName(form.fields, /страница/i);
    if (pageField?.CODE && !payload[pageField.CODE]) {
      payload[pageField.CODE] = window.location.href;
    }

    if (hasRoistatField) {
      payload = addRoistatIdToFormData(payload, form.fields);
    }

    try {
      const response = await ApiClient.formsV2.sentForm(code, payload);
      if (response?.fields) {
        setForm((prev) => ({ ...prev, fields: response.fields }));
      }

      const ctx = { response, fieldValues, popupData };
      if (response?.success) {
        const reply = resolveSuccessReply({
          settings: replySettings,
          name: extractClientName(form.fields, fieldValues),
          number: response.success,
          fallbackText: previousSuccessMessage(successText, response),
        });
        setResult({ isSuccess: true, message: "", reply });
        if (goalSent) {
          reachGoal(goalSent);
        }
        onSuccess?.(ctx);
      } else if (response?.message) {
        setResult({ isSuccess: false, message: response.message });
      }

      onAfterSubmit?.(ctx);
    } catch (e) {
      setResult({
        isSuccess: false,
        message: e?.message || "Ошибка отправки формы",
      });
      onAfterSubmit?.({ response: null, fieldValues, popupData });
    } finally {
      if (hasRoistatField) {
        checkAndLogRoistatId(code, {
          formData: JSON.stringify(payload || {}),
          formName: code,
        }).catch(() => {});
      }
      setIsProcessing(false);
    }
  }, [
    checkboxFields,
    checkboxState,
    code,
    extraData,
    fieldValues,
    form,
    goalSent,
    hasRoistatField,
    onAfterSubmit,
    onSuccess,
    popupData,
    replySettings,
    successText,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        sentForm();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sentForm]);

  const isDisabled = checkboxFields
    .filter((cb) => cb.REQUIRED)
    .some((cb) => !checkboxState[cb.CODE]);

  const submitButtonElement = useMemo(
    () => (
      <Button
        test="submitButton"
        isDisabled={isDisabled}
        onClick={() => sentForm()}
        isLoading={isProcessing}
        label={form.button}
      />
    ),
    [form.button, isDisabled, isProcessing, sentForm],
  );

  return {
    form,
    isLoading,
    isProcessing,
    result,
    fieldValues,
    setFieldValues,
    checkboxState,
    setCheckboxState,
    agreeError,
    sentForm,
    inputFieldElements,
    checkboxOfferInner,
    submitButtonElement,
  };
}
